/**
 * NoExperiments Component
 *
 * A modern screen displayed when no experiments are available.
 * Uses the same light color palette as the rest of the experiment.
 */
import React from "react";

export function NoExperiments() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8 relative overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Subtle floating shapes */}
        <div
          className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse"
          style={{ backgroundColor: "rgba(59, 130, 246, 0.03)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse"
          style={{ backgroundColor: "rgba(59, 130, 246, 0.02)", animationDelay: "1s" }}
        />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-2xl w-full">
        {/* Logo/Icon area */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            {/* Outer ring animation */}
            <div
              className="absolute inset-0 w-24 h-24 rounded-full border-2 border-blue-200 opacity-50 animate-ping"
              style={{ animationDuration: "3s" }}
            />
            <div
              className="absolute inset-0 w-24 h-24 rounded-full border border-blue-300 opacity-30 animate-ping"
              style={{ animationDuration: "2s", animationDelay: "0.5s" }}
            />

            {/* Main icon container */}
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <BrainIcon className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-center mb-2 text-gray-900">
          Trace Lab
        </h1>

        {/* Subtitle */}
        <p className="text-xl text-center text-gray-500 mb-8 font-light">
          University of Cambridge
        </p>

        {/* Status card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-8">
          {/* Status indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: "0.2s" }} />
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" style={{ animationDelay: "0.4s" }} />
            </div>
            <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">
              Awaiting Session
            </span>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-4">
            No Active Experiments
          </h2>

          <p className="text-gray-600 text-center mb-8 leading-relaxed">
            Our research platform is currently preparing for the next session.
            Experiments run at scheduled times with limited participant slots.
          </p>

          {/* Info grid */}
          <div className="grid grid-cols-3 gap-4">
            <InfoCard
              icon={<UsersIcon />}
              label="Participants"
              value="2 per session"
            />
            <InfoCard
              icon={<ClockIcon />}
              label="Duration"
              value="30-35 min"
            />
            <InfoCard
              icon={<ShieldIcon />}
              label="Data"
              value="Anonymous"
            />
          </div>
        </div>

        {/* Action section */}
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <InfoIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-gray-900 font-medium mb-1">What to do next</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Please check with the research team for available session times,
                or try again later. If you have a specific participant link,
                ensure you're using the correct URL.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-gray-400 text-sm">
            Trace Lab Research Platform
          </p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-600 text-xs font-medium">
              System Online
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
        {icon}
      </div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-gray-900 font-medium text-sm">{value}</p>
    </div>
  );
}

// Icons
function BrainIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className || "w-4 h-4"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function InfoIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}
